import { useEffect, useId, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAtomValue } from "jotai";
import { Alert } from "@common/Alert";
import { Button } from "@common/index";
import { ComponentColor, ComponentSize } from "@common/types";
import { usePaymentApi } from "@/hooks/apiHooks/usePaymentApi";
import { useDialogFocusTrap } from "@/hooks/useDialogFocusTrap";
import { isFetchingPaymentSettingsAtom } from "@/store/paymentAtoms";

interface ReturnedPaymentFeeModalProps {
    isProcessing: boolean;
    // Inline failure copy from the parent's POST (contracted 402/409 outcomes).
    // The dialog stays open on failure so the admin can read it.
    errorMessage: string | null;
    // A do-not-retry outcome for this order (the fee may have been, or was,
    // collected): Charge Fee stays disabled, and errorMessage says why.
    isChargeBlocked: boolean;
    onConfirm: (amount: number) => void;
    onClose: () => void;
}

// The numeric input is kept as a string in the form (matches productFormSchema)
// and parsed on submit.
const schema = z.object({
    amount: z
        .string()
        .trim()
        .min(1, "Amount is required")
        .refine((val) => Number.isFinite(Number(val)) && Number(val) > 0, {
            message: "Amount must be greater than zero",
        }),
});

type FormValues = z.infer<typeof schema>;

export const ReturnedPaymentFeeModal = ({
    isProcessing,
    errorMessage,
    isChargeBlocked,
    onConfirm,
    onClose,
}: ReturnedPaymentFeeModalProps) => {
    const panelRef = useRef<HTMLDivElement>(null);
    const titleId = useId();
    const { getPaymentSettings } = usePaymentApi();
    const isFetchingSettings = useAtomValue(isFetchingPaymentSettingsAtom);
    // True once the admin has typed in the amount field. The prefill guard
    // keys on this, NOT on current emptiness: a late-resolving settings fetch
    // must not inject the default into a field the admin touched and then
    // cleared mid-typing (2026-08-28 review F5). A ref, not RHF's isDirty —
    // formState read inside a later .then is a stale first-render closure.
    const amountTouchedRef = useRef(false);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: { amount: "" },
    });

    // Lazily fetch the configured default fee when the dialog opens and
    // pre-fill the (editable) amount — but never clobber a touched field.
    useEffect(() => {
        let cancelled = false;

        getPaymentSettings()
            .then((settings) => {
                if (!cancelled && !amountTouchedRef.current) {
                    reset({ amount: String(settings.returnedPaymentFee) });
                }
            })
            .catch(() => {
                // useApi already toasted; the admin can type the amount.
            });

        return () => {
            cancelled = true;
        };
    }, [getPaymentSettings, reset]);

    // isProcessing refuses Escape: never close over an in-flight charge. A
    // payment_unconfirmed/held rejection renders its do-not-retry copy INLINE
    // (the toast is suppressed by design), and that copy must land on an open
    // dialog — closed, the admin would not see it until they reopened it.
    //
    // closeOnBackdropClick false is a deliberate divergence from the CLAUDE.md
    // overlay contract ("closes on backdrop click"): a money dialog must not
    // be dismissed by an accidental click — an in-flight charge or inline
    // failure copy would vanish from view (2026-08-28 review F3).
    const { backdropProps, panelProps } = useDialogFocusTrap({
        panelRef,
        isProcessing,
        onRequestClose: onClose,
        closeOnBackdropClick: false,
    });

    const onSubmit = (values: FormValues) => {
        onConfirm(Number(values.amount));
    };

    // Spread below with onChange wrapped, so typing marks the field touched.
    const amountField = register("amount");

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            {...backdropProps}
        >
            <div
                ref={panelRef}
                {...panelProps}
                className="w-full max-w-md rounded-lg bg-base-100 p-6 shadow-xl focus:outline-none"
            >
                <h2 id={titleId} className="mb-4 text-lg font-bold">
                    Charge Returned-Payment Fee
                </h2>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div>
                        <label htmlFor="feeAmount" className="label">
                            <span className="label-text">Amount</span>
                        </label>
                        <div className="flex items-center gap-2">
                            <span aria-hidden="true">$</span>
                            <input
                                id="feeAmount"
                                type="number"
                                step="0.01"
                                min="0"
                                inputMode="decimal"
                                placeholder={
                                    isFetchingSettings ? "Loading…" : undefined
                                }
                                {...amountField}
                                onChange={(event) => {
                                    amountTouchedRef.current = true;
                                    void amountField.onChange(event);
                                }}
                                className="input input-bordered w-full"
                            />
                        </div>
                        {errors.amount && (
                            <p className="mt-1 text-sm text-error">
                                {errors.amount.message}
                            </p>
                        )}
                    </div>
                    {errorMessage && (
                        <Alert color={ComponentColor.Error}>
                            {errorMessage}
                        </Alert>
                    )}
                    <div className="flex justify-end gap-3 pt-2">
                        <Button
                            type="button"
                            color={ComponentColor.Ghost}
                            size={ComponentSize.SM}
                            // text-neutral: btn-ghost on bg-base-100 is
                            // white-on-white under the light theme's
                            // --color-base-content quirk (Pagination precedent).
                            className="text-neutral"
                            onClick={onClose}
                            disabled={isProcessing}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            color={ComponentColor.Accent}
                            size={ComponentSize.SM}
                            disabled={isProcessing || isChargeBlocked}
                        >
                            {isProcessing ? "Charging…" : "Charge Fee"}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};
